import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Calendar, Clock, MapPin, Check, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Next 14 business days (skip weekends). Used to render the date picker.
function nextBusinessDays(count = 14) {
  const out = [];
  let d = new Date();
  d.setDate(d.getDate() + 1); // skip today
  while (out.length < count) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) out.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

const TIME_SLOTS = ["8:00 AM", "10:00 AM", "12:00 PM", "2:00 PM", "4:00 PM"];

/**
 * Full-screen "Book a Site Walk" modal.
 * Props: open (bool), onClose (fn)
 * Submits a lead with source="site-walk-request" and highlights preferred date/time in the email.
 */
export default function SiteWalkModal({ open, onClose }) {
  const days = nextBusinessDays(14);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    property_address: "", property_city: "", property_state: "NY", property_zip: "",
    property_type: "Commercial", property_size: "medium", message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  function change(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    if (!date || !time) {
      toast.error("Please pick a date and time.");
      return;
    }
    setSending(true);
    try {
      const preferred = `${date.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })} · ${time}`;
      await axios.post(`${API}/leads`, {
        ...form,
        services: ["Site Walk Request"],
        preferred_date: preferred,
        message: form.message
          ? `${form.message}\n\nRequested site walk: ${preferred}`
          : `Requested site walk: ${preferred}`,
        source: "site-walk-request",
      });
      setSent(true);
      toast.success("Site walk requested. We'll confirm within 1 business hour.");
    } catch {
      toast.error("Could not send — please call (833) 8BESPOKE.");
    } finally {
      setSending(false);
    }
  }

  function close() {
    // reset for next open
    setTimeout(() => {
      setSent(false);
      setDate(null);
      setTime(null);
      setForm({ name: "", email: "", phone: "", company: "", property_address: "", property_city: "", property_state: "NY", property_zip: "", property_type: "Commercial", property_size: "medium", message: "" });
    }, 400);
    onClose?.();
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
          style={{ background: "rgba(6,6,8,0.85)", backdropFilter: "blur(14px)" }}
          onClick={close}
          data-testid="site-walk-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 10 }}
            transition={{ duration: 0.32, ease: [0.19, 1, 0.22, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto"
            style={{
              background: "#0b0b0d",
              border: "1px solid rgba(197,160,89,0.28)",
              borderRadius: 24,
              boxShadow: "0 40px 120px rgba(0,0,0,0.65), 0 0 0 1px rgba(197,160,89,0.08)",
            }}
          >
            {/* Close */}
            <button
              onClick={close}
              className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center rounded-full transition-colors z-10"
              style={{ background: "rgba(255,255,255,0.06)", color: "white" }}
              data-testid="site-walk-close"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            {sent ? (
              <div className="p-10 md:p-14 text-center">
                <Check size={44} style={{ color: "var(--gold)" }} strokeWidth={1.4} className="mx-auto mb-6" />
                <h3 className="font-display text-white text-3xl md:text-4xl" style={{ letterSpacing: "-0.02em" }}>
                  Site walk requested.
                </h3>
                <p className="mt-5 font-body max-w-lg mx-auto" style={{ color: "rgba(255,255,255,0.75)" }}>
                  A founder will call to confirm your slot within one business hour. We'll bring photos, insurance certificate, and a walk-through checklist.
                </p>
                <button
                  onClick={close}
                  className="mt-9 inline-flex items-center gap-2 px-8 py-3 font-mono text-xs uppercase tracking-[0.3em]"
                  style={{ border: "1px solid rgba(255,255,255,0.28)", color: "white", borderRadius: 999 }}
                  data-testid="site-walk-done"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-6 md:p-10">
                <div className="mb-8">
                  <div className="font-mono text-[10px] uppercase tracking-[0.5em]" style={{ color: "var(--gold)" }}>
                    Complimentary · 30 minutes
                  </div>
                  <h3 className="mt-3 font-display text-white text-3xl md:text-4xl" style={{ letterSpacing: "-0.02em", lineHeight: 1.05 }}>
                    Book a Site Walk
                  </h3>
                  <p className="mt-3 font-body text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
                    A founder walks your property with you — measures glass, tests water, spots issues. Zero pressure, zero cost, honest scope.
                  </p>
                </div>

                {/* Date picker */}
                <div className="mb-7">
                  <label className="font-mono text-[10px] uppercase tracking-[0.4em] block mb-3" style={{ color: "rgba(255,255,255,0.55)" }}>
                    <Calendar size={12} className="inline mr-2" style={{ color: "var(--gold)", marginTop: -2 }} /> Pick a Date
                  </label>
                  <div className="grid grid-cols-4 md:grid-cols-7 gap-2" data-testid="site-walk-date-grid">
                    {days.map((d) => {
                      const active = date && d.toDateString() === date.toDateString();
                      return (
                        <button
                          key={d.toISOString()}
                          type="button"
                          onClick={() => setDate(d)}
                          data-testid={`site-walk-date-${d.toISOString().slice(0,10)}`}
                          className="py-3 px-2 text-center transition-all"
                          style={{
                            border: active ? "1px solid var(--gold)" : "1px solid rgba(255,255,255,0.10)",
                            background: active ? "rgba(197,160,89,0.16)" : "rgba(255,255,255,0.02)",
                            borderRadius: 12,
                          }}
                        >
                          <div className="font-mono text-[9px] uppercase tracking-[0.24em]" style={{ color: active ? "var(--gold)" : "rgba(255,255,255,0.5)" }}>
                            {d.toLocaleDateString("en-US", { weekday: "short" })}
                          </div>
                          <div className="mt-1 font-display text-lg" style={{ color: active ? "white" : "rgba(255,255,255,0.85)" }}>
                            {d.getDate()}
                          </div>
                          <div className="font-mono text-[9px]" style={{ color: "rgba(255,255,255,0.4)" }}>
                            {d.toLocaleDateString("en-US", { month: "short" })}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Time picker */}
                {date && (
                  <div className="mb-7">
                    <label className="font-mono text-[10px] uppercase tracking-[0.4em] block mb-3" style={{ color: "rgba(255,255,255,0.55)" }}>
                      <Clock size={12} className="inline mr-2" style={{ color: "var(--gold)", marginTop: -2 }} /> Pick a Time
                    </label>
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-2" data-testid="site-walk-time-grid">
                      {TIME_SLOTS.map((t) => {
                        const active = time === t;
                        return (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setTime(t)}
                            data-testid={`site-walk-time-${t.replace(/[:\s]/g, '')}`}
                            className="py-2.5 font-mono text-[10px] uppercase tracking-[0.24em] transition-all"
                            style={{
                              border: active ? "1px solid var(--gold)" : "1px solid rgba(255,255,255,0.10)",
                              background: active ? "rgba(197,160,89,0.16)" : "rgba(255,255,255,0.02)",
                              color: active ? "var(--gold)" : "rgba(255,255,255,0.75)",
                              borderRadius: 10,
                            }}
                          >
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Contact form */}
                <div className="grid md:grid-cols-2 gap-4">
                  <ModalField label="Full Name" name="name" value={form.name} onChange={change} required />
                  <ModalField label="Company" name="company" value={form.company} onChange={change} />
                  <ModalField label="Email" type="email" name="email" value={form.email} onChange={change} required />
                  <ModalField label="Phone" name="phone" value={form.phone} onChange={change} required />
                </div>
                <div className="mt-4">
                  <ModalField label="Property Street Address" name="property_address" value={form.property_address} onChange={change} placeholder="e.g. 145 Broadway" />
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <ModalField label="City" name="property_city" value={form.property_city} onChange={change} />
                  <ModalField label="State" name="property_state" value={form.property_state} onChange={change} />
                  <ModalField label="Zip" name="property_zip" value={form.property_zip} onChange={change} />
                </div>

                {/* Submit */}
                <div className="mt-9 flex items-center justify-between flex-wrap gap-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.32em] max-w-xs" style={{ color: "rgba(255,255,255,0.5)" }}>
                    No credit card · Zero commitment
                  </p>
                  <button
                    type="submit"
                    disabled={sending}
                    className="inline-flex items-center gap-3 px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] transition-transform duration-300 hover:scale-[1.03] disabled:opacity-60"
                    style={{
                      background: "linear-gradient(180deg, #EFC97B 0%, #C5A059 100%)",
                      color: "#0b0b0d",
                      borderRadius: 999,
                      fontWeight: 600,
                    }}
                    data-testid="site-walk-submit"
                  >
                    {sending ? "Sending…" : "Request Site Walk"} <ArrowUpRight size={14} />
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ModalField({ label, name, value, onChange, type = "text", required = false, placeholder = "" }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.4em] block mb-2" style={{ color: "rgba(255,255,255,0.55)" }}>
        {label} {required && <span style={{ color: "var(--gold)" }}>*</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(name, e.target.value)}
        data-testid={`site-walk-field-${name}`}
        className="w-full bg-transparent px-4 py-3 font-body focus:outline-none focus:border-yellow-500 transition-colors"
        style={{ borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", color: "white" }}
      />
    </div>
  );
}
